import type { Track } from '$lib/models/track';
import { get, writable, type Writable } from 'svelte/store';
import { browser } from '$app/environment';
import { timeToSeconds } from '$lib/utils/time';
import { getRandomTrack } from '$lib/utils/random';

export type PlayerStore = {
  subscribe: Writable<PlayerState>['subscribe'];
  play: (track: Track, startTime?: number, parentAsset?: Track) => void;
  setQueue: (queue: Track[]) => void;
  seek: (time: number) => void;
  pause: () => void;
  resume: () => void;
  clear: () => void;
  next: () => void;
  previous: () => void;
};

export interface PlayerState {
  id: number;
  track: Track | null;
  parentAsset: Track | null;
  progress: number; // From 0 to 1
  duration: number;
  currentTime: number;
  status: 'playing' | 'paused' | 'stopped' | 'loading';
  autoplay: boolean;
  queue: Track[];
  queueIndex?: number;
}

export function createPlayer(): PlayerStore {
  const store = writable<PlayerState>({
    id: 0,
    track: null,
    parentAsset: null,
    duration: 0,
    progress: 0,
    currentTime: 0,
    status: 'stopped',
    autoplay: false,
    queue: [],
  });
  let { subscribe, set, update } = store;

  const autoplayLimit = 5;
  let autoplayCounter = 0;

  let player: HTMLAudioElement;

  const attachListeners = () => {
    if (player.getAttribute('data-attached')) {
      return;
    }
    player.setAttribute('data-attached', 'true');
    player.addEventListener('timeupdate', function () {
      if (!player.duration) {
        return;
      }
      update((state) => {
        return {
          ...state,
          progress: player.currentTime / player.duration,
          currentTime: player.currentTime,
        };
      });
    });
    player.addEventListener('playing', function () {
      update((state) => {
        return {
          ...state,
          status: 'playing',
        };
      });
    });
    player.addEventListener('ended', function () {
      update((state) => {
        if (state.autoplay && autoplayCounter < autoplayLimit) {
          autoplayCounter += 1;
          getRandomTrack().then((nextTrack) => {
            playTrack(nextTrack, 0);
          });
        }
        return {
          ...state,
          progress: 0,
          currentTime: 0,
          status: 'stopped',
        };
      });
    });
  };

  const playPromise = () => {
    player
      .play()
      .then()
      .catch((error) => {
        if (error.name !== 'AbortError') {
          update((state) => {
            return {
              ...state,
              status: 'stopped',
            };
          });
        }
        console.log(error);
      });
  };

  const playTrack = (track: Track, startTime = 0, parentAsset: Track | null = null) => {
    player.pause();
    player.currentTime = 0;
    player.src = '';
    update((state) => {
      let queueIndex = undefined;
      if (state.queue) {
        queueIndex = state.queue.findIndex((t) => t.id === track.id);
      }
      if (queueIndex === -1) {
        queueIndex = undefined;
      }
      return {
        ...state,
        id: track.id,
        track,
        parentAsset,
        progress: startTime ? startTime / timeToSeconds(track.duration) : 0,
        duration: timeToSeconds(track.duration),
        currentTime: startTime,
        status: 'loading',
        queueIndex,
      };
    });
    player.src = track.mp3 + '#t=' + startTime;
    playPromise();
  };

  if (browser) {
    player = document.getElementById('player') as HTMLAudioElement;
    if (player) {
      attachListeners();
    }
  }

  return {
    subscribe,

    play: (track: Track, startTime = 0, parentAsset: Track | null = null) => {
      if (!browser) {
        return;
      }
      autoplayCounter = 0;
      playTrack(track, startTime, parentAsset);
    },

    setQueue: (queue: Track[]) => {
      if (!browser) {
        return;
      }
      update((state) => {
        if (queue.length === 0) {
          return {
            ...state,
            queue: [],
            queueIndex: undefined,
          };
        }
        let queueIndex: number | undefined = queue.findIndex((t) => t.id === state.track?.id);
        if (queueIndex === -1) {
          queueIndex = undefined;
        }
        return {
          ...state,
          queue,
          queueIndex,
        };
      });
    },

    seek: (time: number) => {
      if (!browser) {
        return;
      }
      update((state) => {
        if (!state.track) {
          return state;
        }
        player.currentTime = time;
        return {
          ...state,
          progress: time / state.duration,
          currentTime: time,
        };
      });
    },

    pause: () => {
      if (!browser) {
        return;
      }
      player.pause();
      update((state) => {
        return {
          ...state,
          status: 'paused',
        };
      });
    },

    resume: () => {
      if (!browser) {
        return;
      }
      update((state) => {
        if (!state.track) {
          return state;
        }
        playPromise();
        return {
          ...state,
          status: 'playing',
        };
      });
    },

    clear: () => {
      if (!browser) {
        return;
      }
      autoplayCounter = 0;
      player.pause();
      player.currentTime = 0;
      player.src = '';
      update((state) => {
        return {
          ...state,
          id: 0,
          track: null,
          progress: 0,
          duration: 0,
          currentTime: 0,
          status: 'stopped',
        };
      });
    },

    next: () => {
      if (!browser) return;
      let state = get(store);
      if (!state.queue || state.queue.length === 0) return state;
      if (state.queueIndex === undefined || typeof state.queueIndex !== 'number') {
        const nextTrack = state.queue[0];
        playTrack(nextTrack);
        return;
      }
      if (state.queueIndex >= state.queue.length - 1) {
        return;
      }
      const nextIndex = state.queueIndex + 1;
      const nextTrack = state.queue[nextIndex];
      playTrack(nextTrack);
    },

    previous: () => {
      if (!browser) return;
      let state = get(store);
      if (!state.queue || typeof state.queueIndex !== 'number' || state.queue.length === 0 || state.queueIndex <= 0) return state;
      const prevIndex = state.queueIndex - 1;
      const prevTrack = state.queue[prevIndex];
      playTrack(prevTrack);
    },
  };
}

export const player = createPlayer();
